import { useState } from 'react';
import { CaretLeft, CaretRight, MapPin, SlidersHorizontal, Tag } from '@phosphor-icons/react';
import Modal from '../../../shared/components/Modal';

type TagOption = {
  id: string;
  name: string;
  type?: string;
};

type TagPlacesModalProps = {
  tag: TagOption;
  places: any[];
  t: any;
  onClose: () => void;
  onOpen: (place: any) => void;
};

const ITEMS_PER_PAGE = 8;

export default function TagPlacesModal({
  tag,
  places,
  t,
  onClose,
  onOpen,
}: TagPlacesModalProps) {
  const [sort, setSort] = useState<
    'name-asc' | 'name-desc' | 'rating-desc' | 'rating-asc'
  >('name-asc');
  const [page, setPage] = useState(1);
  const matches = places
    .filter((place) => tag.type
      ? place.type === tag.type
      : (place.tags || []).some((item: TagOption) => item.id === tag.id))
    .sort((first, second) => {
      const nameComparison = first.name.localeCompare(
        second.name,
        t.locale,
        { sensitivity: 'base' },
      );
      if (sort === 'rating-desc') {
        return (Number(second.rating) || 0) - (Number(first.rating) || 0)
          || nameComparison;
      }
      if (sort === 'rating-asc') {
        return (Number(first.rating) || 0) - (Number(second.rating) || 0)
          || nameComparison;
      }
      return sort === 'name-desc' ? -nameComparison : nameComparison;
    });
  const totalPages = Math.max(1, Math.ceil(matches.length / ITEMS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const visibleMatches = matches.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );
  const title = (t.tagPlacesTitle || '{name}').replace('{name}', tag.name);
  const heading = (t.tagPlacesTitle || '').replace('{name}', '').trim();

  return (
    <Modal label={title} onClose={onClose}>
      <div className="modal-head tag-places-head">
        <span className="eyebrow coral"><Tag weight="fill" />{t.tags}</span>
        <h2>{heading}</h2>
        <span className="tag-places-active-tag">{tag.name}</span>
      </div>
      <div className="tag-places-toolbar">
        <label className="review-sort tag-places-sort">
          <SlidersHorizontal />
          <select
            value={sort}
            onChange={(event: any) => {
              setSort(event.target.value);
              setPage(1);
            }}
            aria-label={t.sortBy}
          >
            <option value="name-asc">{t.nameAZ}</option>
            <option value="name-desc">{t.nameZA}</option>
            <option value="rating-desc">{t.ratingHigh}</option>
            <option value="rating-asc">{t.ratingLow}</option>
          </select>
        </label>
      </div>
      {matches.length ? (
        <>
          <div className="tag-places-list">
            {visibleMatches.map((place) => (
              <button
                type="button"
                key={place.id}
                onClick={() => onOpen(place)}
              >
                <img src={place.image || '/place-placeholder.svg'} alt="" />
                <span>
                  <b>{place.name}</b>
                  <small><MapPin weight="fill" />{place.address}</small>
                </span>
                <CaretRight />
              </button>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="tag-places-pagination">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                <CaretLeft />{t.previous}
              </button>
              <span>{t.page} <b>{currentPage}</b> {t.of} {totalPages}</span>
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              >
                {t.next}<CaretRight />
              </button>
            </div>
          )}
        </>
      ) : (
        <p className="tag-places-empty">{t.tagPlacesEmpty}</p>
      )}
    </Modal>
  );
}
